import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ForcedCheckinDateRange } from '../forced-checkin.component';
import * as moment from 'moment/moment';
import { NavigationService } from '../../../shared/services/navigation.service';
import { ForcedCheckinResult } from '../shared/forced-checkin-result';
import { catchError, finalize, tap } from 'rxjs/operators';
import { Region } from '../../../user-dashboard/shared/region.model';
import { AuthenticationService } from '../../../shared/services/authentication.service';
import { RegionService } from '../../../user-dashboard/shared/region.service';
import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { ForcedCheckinSearchService } from '../shared/forced-checkin-search.service';
import { Carrier } from '../../../user-dashboard/shared/carrier.model';
import { CarrierService } from '../../../user-dashboard/shared/carrier.service';
import {EMPTY, Observable, range} from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { UserService } from '../../../user-dashboard/shared/user.service';
import { RolesLabelEnum } from '../../../user-dashboard/shared/role.model';
import { LoaderService } from '../../../shared/services/loader.service';
import { LOCAL_STORAGE_KEYS } from '../../../core/constants';

@Component({
  selector: 'app-forced-checkin-bucket',
  templateUrl: './forced-checkin-bucket.component.html',
  styleUrls: ['./forced-checkin-bucket.component.scss']
})
export class ForcedCheckinBucketComponent {

  @Input() regionId: string;
  @Input() selectedPlantGlid: string;
  @Input() showBucket: boolean;
  @Input() dateRange: ForcedCheckinDateRange;
  @Input() forcedCheckinResult: ForcedCheckinResult[];

  @Output() showBucketChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() selectedPlantGlidChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() reloadPlantResults: EventEmitter<void> = new EventEmitter<void>();
  @Output() dateRangeChange: EventEmitter<ForcedCheckinDateRange> = new EventEmitter<ForcedCheckinDateRange>();
  @Output() selectedRegionChange: EventEmitter<Region> = new EventEmitter<Region>();
  @Output() selectedCarrierChange: EventEmitter<Carrier> = new EventEmitter<Carrier>();
  @Output() forcedCheckinResultChange: EventEmitter<ForcedCheckinResult[]> = new EventEmitter<ForcedCheckinResult[]>();

  public dateRanges = ForcedCheckinDateRange;
  public plantSearchFilterValue: string;

  public readonly userIsCarrierManager: boolean;
  public resultTab = false;
  public noResult = true;
  public noFirstSearch = true;
  public loadingCarriers = true;
  public loadingRegions = true;
  public totalForcedCheckins = 0;

  public regions: Region[] = [];
  private _selectedRegion: Region = null;

  public carriers: Carrier[] = [];
  private _selectedCarrier: Carrier = null;


  get selectedRegion(): Region {
    return this._selectedRegion;
  }

  set selectedRegion(region: Region) {
    if (!region) {
      this.clearRegion();
      return;
    }
    this._selectedRegion = region;
    localStorage.setItem(LOCAL_STORAGE_KEYS.FORCED_CHECKIN_REGION, this._selectedRegion.id);
  }

  get selectedCarrier(): Carrier {
    return this._selectedCarrier;
  }

  set selectedCarrier(carrier: Carrier) {
    if (!carrier) {
      this.clearCarrier();
      return;
    }
    this._selectedCarrier = carrier;
    localStorage.setItem(LOCAL_STORAGE_KEYS.FORCED_CHECKIN_CARRIER, this._selectedCarrier.id);
  }

  constructor(
    private loader: LoaderService,
    private userService: UserService,
    private carrierService: CarrierService,
    private regionService: RegionService,
    private navigationService: NavigationService,
    private forcedCheckinSearchService: ForcedCheckinSearchService,
    private authService: AuthenticationService,
    private errorHandler: ErrorHandlerService
  ) {
    this.userIsCarrierManager = this.authService.canReadCarrierForcedCheckin();
    const role = this.getUserRole();
    this.initSelectableDataForRole(role);
  }

  private getUserRole(): RolesLabelEnum {
    if (this.authService.canManageRegions()) {
      return RolesLabelEnum.ADMINISTRATOR;
    } else if (this.authService.canReadRegions()) {
      return RolesLabelEnum.CHEP_MANAGER;
    } else if (this.userIsCarrierManager) {
      return RolesLabelEnum.CARRIER_MANAGER;
    }
    throw Error('The current user cannot access this page');
  }

  private initSelectableDataForRole(role: RolesLabelEnum): void {
    switch (role) {
      case RolesLabelEnum.ADMINISTRATOR:
        this.fetchRegions();
        this.handleCarriersResponse(this.carrierService.getAllLight());
        break;
      case RolesLabelEnum.CHEP_MANAGER:
        this.fetchRegions();
        this.userService
          .getSelf()
          .subscribe(info =>
            this.handleCarriersResponse(this.carrierService.getAllLightByRegions(info.allowedRegions))
          );
        break;
      case RolesLabelEnum.CARRIER_MANAGER:
        this._selectedCarrier = this.authService.getUserProfile().carrier;
        break;
    }
  }

  private fetchRegions(): void {
    this.regionService.getOwnRegionsLight()
      .pipe(
        tap(regions => this.selectRegionFromLocalStorage(regions)),
        catchError( (e: HttpErrorResponse) => {
          this.errorHandler.handleHttpError(e, 'forced-checkin.errors.fetch-regions');
          return EMPTY;
        }),
        finalize(() => this.loadingRegions = false)
      )
      .subscribe();
  }

  private handleCarriersResponse(carrierFetch: Observable<Carrier[]>): void {
    carrierFetch
      .pipe(
        tap(carriers => this.selectCarrierFromLocalStorage(carriers)),
        catchError( (e: HttpErrorResponse) => {
          this.errorHandler.handleHttpError(e, 'forced-checkin.errors.fetch-carriers');
          return EMPTY;
        }),
        finalize(() => this.loadingCarriers = false)
      )
      .subscribe();
  }

  public searchPlants(): void {
    if ((this._selectedRegion && !this._selectedCarrier) || (!this._selectedRegion && this._selectedCarrier)) {
      this.loader.startLoad();
      this.selectedRegionChange.emit(this._selectedRegion);
      this.selectedCarrierChange.emit(this._selectedCarrier);
      this.forcedCheckinSearchService.searchByCriteria(
        this._selectedRegion?.id,
        this._selectedCarrier?.id,
        this.plantSearchFilterValue,
        this.dateRange
      )
        .pipe(finalize(() => this.loader.stopLoad()))
        .subscribe((res: ForcedCheckinResult[]) => {
          this.noResult = !res || res.length === 0;
          this.resultTab = true;
          this.updateSelectedPlant(res);
          this.noFirstSearch = false;
          this.totalForcedCheckins = res?.reduce((total, item) => {
            return total + item.nbForcedCheckin;
          }, 0) || 0;
          this.forcedCheckinResultChange.emit(res)
        });
    }
  }

  private updateSelectedPlant(searchResult: ForcedCheckinResult[]) {
    if (this.selectedPlantGlid != null) {
      const selectedGlidInResult = searchResult.some(result => result.glid === this.selectedPlantGlid);
      if (selectedGlidInResult) {
        this.reloadPlantResults.emit();
      } else {
        this.selectedPlantGlidChange.emit(null);
      }
    }
  }

  public setDateRange(range: ForcedCheckinDateRange): void {
    this.dateRangeChange.emit(range);
  }

  public goToLocationConfig(glid: string): void {
    this.navigationService
      .localLoads()
      .locations({
        queryParams: {
          glid: glid
        }
      }).go();
  }

  public goToForcedCheckinLogEvents(glid: string): void {
    this.navigationService.logEvents({
      queryParams: {
        event: 'CHECKIN_GPS_POSITION_FORCED',
        locationRef: glid,
        searchOnNavigate: true,
        start_date: moment().subtract(
          1,
          this.buildMomentUnitFromDateRange()
        ).format(),
        end_date: moment().format()
      }
    }).go();
  }

  private buildMomentUnitFromDateRange() {
    switch (this.dateRange) {
      case ForcedCheckinDateRange.ONE_DAY:
        return 'day';
      case ForcedCheckinDateRange.ONE_WEEK:
        return 'week';
      case ForcedCheckinDateRange.ONE_MONTH:
        return 'month';
    }
  }

  public clearInput(): void {
    this.plantSearchFilterValue = '';
  }

  public resetCriteria(): void {
    this.plantSearchFilterValue = '';
    this.dateRangeChange.emit(ForcedCheckinDateRange.ONE_DAY);
    this._selectedRegion = null;
    if (!this.userIsCarrierManager) {
      this._selectedCarrier = null;
    }
  }

  public selectResultTab(): void {
    if (!this.noFirstSearch) {
      this.resultTab = true;
    }
  }

  private selectCarrierFromLocalStorage(carriers: Carrier[]) {
    this.carriers = carriers;
    const carrierIdFromStorage = localStorage.getItem(LOCAL_STORAGE_KEYS.FORCED_CHECKIN_CARRIER);
    this.selectedCarrier = carriers.find(c => c.id === carrierIdFromStorage);
  }

  public clearCarrier(): void {
    this._selectedCarrier = null;
    localStorage.removeItem(LOCAL_STORAGE_KEYS.FORCED_CHECKIN_CARRIER);
  }

  private selectRegionFromLocalStorage(regions: Region[]) {
    this.regions = regions;
    const regionIdFromStorage = localStorage.getItem(LOCAL_STORAGE_KEYS.FORCED_CHECKIN_REGION);
    this.selectedRegion = regions.find( r => r.id === regionIdFromStorage);
  }

  public clearRegion(): void {
    this._selectedRegion = null;
    localStorage.removeItem(LOCAL_STORAGE_KEYS.FORCED_CHECKIN_REGION);
  }
}
